import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft, Loader2, Package } from "lucide-react";
import { Progress } from "@/components/ui/progress";

interface OptimizedOrderDetailsLoadingStateProps {
  onBack: () => void;
  progress?: number;
  loadingStage?: string;
  showProductSkeletons?: boolean;
}

export const OptimizedOrderDetailsLoadingState = ({ 
  onBack, 
  progress = 0,
  loadingStage = "Initializing...",
  showProductSkeletons = false
}: OptimizedOrderDetailsLoadingStateProps) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      <div className="w-full px-2 md:px-4 lg:container lg:mx-auto py-2 md:py-6">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex items-center gap-3 md:gap-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={onBack}
              className="shrink-0 p-2 hover:bg-gray-100"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Back</span>
            </Button>
            <div className="min-w-0 flex-1">
              <Skeleton className="h-6 md:h-7 w-48 mb-1" />
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
        </div>

        {/* Enhanced Loading Content */}
        <div className="space-y-4 md:space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-4 md:p-6">
            <div className="flex items-center justify-center gap-3 mb-6">
              <Loader2 className="w-6 md:w-8 h-6 md:h-8 animate-spin text-blue-600" />
              <div className="text-center">
                <div className="text-lg md:text-xl font-semibold text-gray-700 mb-1">
                  Loading Order Details
                </div>
                <div className="text-xs md:text-sm text-gray-500 mb-3">
                  {loadingStage}
                </div>
                {progress > 0 && (
                  <div className="w-64 mx-auto">
                    <Progress value={progress} className="h-2" />
                    <div className="text-xs text-gray-400 mt-1">{progress}% complete</div>
                  </div>
                )}
              </div>
            </div>

            {/* Order Statistics Skeleton */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="text-center p-3 bg-gray-50 rounded-lg">
                  <Skeleton className="h-8 w-12 mx-auto mb-2" />
                  <Skeleton className="h-4 w-20 mx-auto" />
                </div>
              ))}
            </div>

            {/* Search and Filter Skeleton */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Skeleton className="h-10 flex-1" />
              <Skeleton className="h-10 w-32" />
              <Skeleton className="h-10 w-24" />
            </div>

            {/* Products Loading - Show skeleton grid if enabled */}
            {showProductSkeletons && (
              <div className="space-y-3">
                <div className="flex items-center gap-2 mb-4">
                  <Package className="w-5 h-5 text-gray-400" />
                  <span className="text-sm text-gray-600">Loading products...</span>
                </div>
                
                {/* Mobile view skeletons */}
                <div className="block md:hidden space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="border rounded-lg p-4">
                      <div className="flex gap-3">
                        <Skeleton className="w-16 h-16 rounded" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-5 w-3/4" />
                          <Skeleton className="h-4 w-1/2" />
                          <div className="flex gap-2">
                            <Skeleton className="h-6 w-16" />
                            <Skeleton className="h-6 w-20" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view skeletons */}
                <div className="hidden md:block">
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-50 border-b p-3">
                      <div className="grid grid-cols-6 gap-4">
                        {['Product', 'Barcode', 'Quantity', 'Photos', 'POS Status', 'Actions'].map((header) => (
                          <Skeleton key={header} className="h-4 w-20" />
                        ))}
                      </div>
                    </div>
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div key={i} className="border-b p-3">
                        <div className="grid grid-cols-6 gap-4 items-center">
                          <Skeleton className="h-5 w-32" />
                          <Skeleton className="h-4 w-24" />
                          <Skeleton className="h-4 w-16" />
                          <Skeleton className="h-8 w-8 rounded" />
                          <Skeleton className="h-6 w-20 rounded-full" />
                          <div className="flex gap-2">
                            <Skeleton className="h-8 w-8" />
                            <Skeleton className="h-8 w-8" />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};